import React, { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useState } from 'react';

export type DocInfo = Record<string, unknown>;

type DocAreaContextValue = {
  docInfo: DocInfo;
  setDocInfo: Dispatch<SetStateAction<DocInfo>>;
  isDocAreaOpen: boolean;
  closeDocArea: () => void;
  showDocArea: boolean;
  setShowDocArea: Dispatch<SetStateAction<boolean>>;
  mid: string;
  setMid: Dispatch<SetStateAction<string>>;
  docopen: boolean;
  setDocopen: Dispatch<SetStateAction<boolean>>;
  pressed: boolean;
  setPressed: Dispatch<SetStateAction<boolean>>;
};

const DocAreaContext = createContext<DocAreaContextValue | null>(null);

export function DocAreaProvider({ children }: { children: ReactNode }) {
  const [docInfo, setDocInfo] = useState<DocInfo>({});
  const [showDocArea, setShowDocArea] = useState(false);
  const [mid, setMid] = useState('');
  const [docopen, setDocopen] = useState(true);
  const [pressed, setPressed] = useState(false);

  const closeDocArea = useCallback(() => {
    setDocInfo({});
    setShowDocArea(false);
  }, []);

  const isDocAreaOpen = Object.keys(docInfo).length > 0;

  return (
    <DocAreaContext.Provider value={{
      docInfo, setDocInfo,
      isDocAreaOpen, closeDocArea,
      showDocArea, setShowDocArea,
      mid, setMid,
      docopen, setDocopen,
      pressed, setPressed,
    }}>
      {children}
    </DocAreaContext.Provider>
  );
}

const noopDocArea: DocAreaContextValue = {
  docInfo: {},
  setDocInfo: () => undefined,
  isDocAreaOpen: false,
  closeDocArea: () => undefined,
  showDocArea: false,
  setShowDocArea: () => undefined,
  mid: '',
  setMid: () => undefined,
  docopen: false,
  setDocopen: () => undefined,
  pressed: false,
  setPressed: () => undefined,
};

export function useDocArea(): DocAreaContextValue {
  const ctx = useContext(DocAreaContext);
  return ctx ?? noopDocArea;
}
